import { useState, useEffect, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useT } from "@/lib/i18n";
import logo from "@/assets/icons/outclose.svg";

interface ScanningPhase {
  title: string;
  detail: string;
  duration: number;
}

interface ScanningScreenProps {
  onScanComplete?: () => void;
}

function jitter(base: number, variance = 0.35): number {
  return base + base * (Math.random() * variance * 2 - variance);
}

export default function ScanningScreen({ onScanComplete }: ScanningScreenProps) {
  const { t } = useT();
  const [currentPhase, setCurrentPhase] = useState(0);
  const [progress, setProgress] = useState(0);
  const [scanDone, setScanDone] = useState(false);
  const speedRef = useRef(1);

  const phases: ScanningPhase[] = [
    { title: t("quiz.scanning.analyzingPreferences"), detail: t("quiz.scanning.analyzingDetail"), duration: 900 },
    { title: t("quiz.scanning.scanningNeighborhood"), detail: t("quiz.scanning.scanningDetail"), duration: 800 },
    { title: t("quiz.scanning.findingPersonalities"), detail: t("quiz.scanning.findingDetail"), duration: 800 },
    { title: t("quiz.scanning.matchingVibes"), detail: t("quiz.scanning.matchingDetail"), duration: 700 },
    { title: t("quiz.scanning.matchesReady"), detail: t("quiz.scanning.matchesReadyDetail"), duration: 600 },
  ];

  const totalDuration = phases.reduce((sum, p) => sum + p.duration, 0);

  // Advance phases
  useEffect(() => {
    if (currentPhase >= phases.length - 1) return;
    const timer = setTimeout(() => setCurrentPhase((p) => p + 1), jitter(phases[currentPhase].duration));
    return () => clearTimeout(timer);
  }, [currentPhase, phases]);

  // Uneven progress bar
  useEffect(() => {
    const interval = 60;
    const speedShift = setInterval(() => { speedRef.current = 0.3 + Math.random() * 1.8; }, 300 + Math.random() * 300);
    const id = setInterval(() => {
      setProgress((prev) => {
        const target = currentPhase >= phases.length - 1 ? 100 : ((currentPhase + 1) / phases.length) * 100;
        const step = (100 / (totalDuration / interval)) * 1.1 * speedRef.current;
        return Math.min(prev + step, target);
      });
    }, interval);
    return () => { clearInterval(id); clearInterval(speedShift); };
  }, [currentPhase, phases.length, totalDuration]);

  // When scanning animation finishes, show celebration
  useEffect(() => {
    if (currentPhase >= phases.length - 1 && !scanDone) {
      const timer = setTimeout(() => setScanDone(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [currentPhase, phases.length, scanDone]);

  // Firework particles — stable across renders
  const fireworkParticles = useMemo(() =>
    Array.from({ length: 30 }).map((_, i) => {
      const angle = (i / 30) * Math.PI * 2 + Math.random() * 0.3;
      const distance = 150 + Math.random() * 250;
      const size = 2 + Math.random() * 3;
      const colors = ["#EF6225", "#81358A", "#36A2AD", "#F47D4D", "#D49BDB"];
      return { angle, distance, size, color: colors[i % colors.length], delay: Math.random() * 0.5, extraDuration: Math.random(), repeatDelay: 2 + Math.random() * 2 };
    }), []);

  // ── Celebration screen ──
  if (scanDone) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center min-h-screen px-6 overflow-hidden"
        style={{ background: "linear-gradient(to bottom, #1a1a1a 0%, #171717 100%)" }}
      >
        {/* Pulsing rings from center */}
        {[0, 1, 2].map((i) => (
          <motion.div
            key={`ring-${i}`}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border pointer-events-none"
            style={{ borderColor: i === 0 ? "rgba(239,98,37,0.15)" : i === 1 ? "rgba(129,53,138,0.12)" : "rgba(54,162,173,0.1)" }}
            initial={{ width: 0, height: 0, opacity: 0.6 }}
            animate={{ width: 600, height: 600, opacity: 0 }}
            transition={{ duration: 3, repeat: Infinity, delay: i * 1, ease: "easeOut" }}
          />
        ))}

        {/* Firework particles */}
        {fireworkParticles.map((p, i) => (
          <motion.div
            key={`fw-${i}`}
            className="absolute top-1/2 left-1/2 rounded-full pointer-events-none"
            style={{ width: p.size, height: p.size, background: p.color }}
            initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
            animate={{
              x: Math.cos(p.angle) * p.distance,
              y: Math.sin(p.angle) * p.distance,
              opacity: [1, 1, 0],
              scale: [0, 1.5, 0],
            }}
            transition={{
              duration: 1.5 + p.extraDuration,
              delay: p.delay,
              repeat: Infinity,
              repeatDelay: p.repeatDelay,
              ease: "easeOut",
            }}
          />
        ))}

        {/* Soft glow behind content */}
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full opacity-20 blur-[120px] pointer-events-none"
          style={{ background: "radial-gradient(circle, #EF6225 0%, #81358A 50%, transparent 70%)" }}
        />

        <div className="relative text-center max-w-md w-full">
          <motion.img
            src={logo}
            alt="Outclose"
            className="w-20 h-20 invert mx-auto mb-10"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          />

          <motion.h1
            className="text-4xl md:text-5xl font-bold text-white leading-tight"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <span className="block text-5xl font-bold mb-4">
              {t("quiz.scanning.celebrationLine1")}
            </span>
            <span className="block text-xl text-dark-muted">
              {t("quiz.scanning.celebrationLine2")}
            </span>
          </motion.h1>

          <motion.button
            onClick={() => onScanComplete?.()}
            className="mt-10 w-full px-6 py-4 bg-white rounded-2xl font-semibold text-neutral-900 hover:bg-neutral-100 transition-colors text-lg"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            {t("common.buttons.signUp")}
          </motion.button>
        </div>
      </div>
    );
  }

  // ── Scanning animation ──
  return (
    <div className="flex items-center justify-center bg-neutral-900 min-h-screen fixed inset-0 z-50">
      <div className="text-center px-6 max-w-md w-full">
        {/* Radar pulse rings */}
        <div className="relative w-40 h-40 mx-auto mb-10">
          <div className="absolute inset-0 flex items-center justify-center">
            <motion.div
              className="w-4 h-4 rounded-full bg-primary-500"
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
            />
          </div>
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              className="absolute inset-0 rounded-full border border-primary-500/40"
              initial={{ scale: 0.1, opacity: 0.8 }}
              animate={{ scale: 1.2, opacity: 0 }}
              transition={{ duration: 2.4, repeat: Infinity, delay: i * 0.8, ease: "easeOut" }}
            />
          ))}
          <motion.div
            className="absolute inset-0"
            animate={{ rotate: 360 }}
            transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
          >
            <div
              className="absolute top-1/2 left-1/2 w-1/2 h-px origin-left"
              style={{ background: "linear-gradient(to right, rgba(239, 98, 37, 0.8), transparent)" }}
            />
          </motion.div>
          {[0.3, 0.6, 0.9].map((scale) => (
            <div
              key={scale}
              className="absolute inset-0 rounded-full border border-neutral-700/30"
              style={{ transform: `scale(${scale})` }}
            />
          ))}
        </div>

        {/* Phase text */}
        <div className="h-20 flex flex-col items-center justify-center">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentPhase}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="text-center"
            >
              <h2 className="text-xl font-semibold text-neutral-100 mb-1.5">
                {phases[currentPhase].title}
              </h2>
              <p className="text-sm text-neutral-500 font-mono">
                {phases[currentPhase].detail}
              </p>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Progress bar */}
        <div className="mt-8 w-full h-1 bg-neutral-800 rounded-full overflow-hidden">
          <motion.div
            className="h-full rounded-full"
            style={{ background: "linear-gradient(to right, #EF6225, #81358A)" }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.1, ease: "linear" }}
          />
        </div>

        {/* Phase dots */}
        <div className="flex justify-center gap-2 mt-4">
          {phases.map((_, i) => (
            <div
              key={i}
              className={`w-1.5 h-1.5 rounded-full transition-colors duration-300 ${
                i <= currentPhase ? "bg-primary-500" : "bg-neutral-700"
              }`}
            />
          ))}
        </div>

        {/* Skip */}
        <motion.button
          onClick={() => onScanComplete?.()}
          className="mt-8 text-xs text-neutral-600 hover:text-neutral-400 transition-colors"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 2 }}
        >
          {t("common.buttons.skip")}
        </motion.button>
      </div>
    </div>
  );
}
